import React from 'react';
import { Clock, Eye, PlayCircle, Star } from 'lucide-react';
import LessonThumbnailSection from '../lesson/LessonThumbnailSection';
import { getLessonDuration, formatDuration, getLessonViews } from '../../utils/videoUtils';

const StarRating = ({ rating, onRate, size = 'small' }) => {
  const starSize = size === 'large' ? 'w-10 h-10' : 'w-4 h-4';
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-1">
      {stars.map((star) => (
        <button
          key={star}
          onClick={() => onRate && onRate(star)}
          className={`${onRate ? 'cursor-pointer hover:scale-125' : 'cursor-default'} transition-all duration-200`}
          disabled={!onRate}
          type="button"
        >
          <Star className={`${starSize} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
};

const StudentLessonCard = ({ lesson, onView, onRate }) => {
  return (
    <div className="group bg-white rounded-[2.5rem] shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden flex flex-col">
      <LessonThumbnailSection lesson={lesson} onClickLesson={onView} />
      <div className="p-8 flex flex-col flex-1 relative">
        <div className="flex items-center gap-2 mb-4">
          <span className="bg-primary-50 text-primary-600 text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border border-primary-100">
            {lesson.course_title}
          </span>
        </div>

        <h3 className="font-black text-gray-900 text-xl mb-3 line-clamp-1 group-hover:text-primary-600 transition-colors tracking-tight">
          {lesson.title}
        </h3>
        <p className="text-gray-500 text-sm line-clamp-2 mb-8 flex-1 leading-relaxed font-medium">
          {lesson.description || 'اضغط لمشاهدة محتوى هذا الدرس التعليمي وتحميل كافة الملفات المرفقة به.'}
        </p>

        <div className="space-y-2 mb-6">
          <div className="flex items-center justify-between text-[9px] font-black">
            <span className="text-gray-400 uppercase tracking-widest">نسبة الإنجاز</span>
            <span className="text-primary-600">{lesson.progress || 0}%</span>
          </div>
          <div className="w-full bg-gray-50 rounded-full h-2 overflow-hidden border border-gray-100">
            <div
              className="bg-primary-600 h-full rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(37,99,235,0.4)]"
              style={{ width: `${lesson.progress || 0}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between py-6 border-t border-gray-50 mt-auto">
          <div className="flex flex-col">
            <span className="text-[9px] text-gray-400 font-black uppercase tracking-tighter mb-0.5">المدرس المحاضر</span>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-[10px] font-black text-primary-600 shadow-inner">
                {lesson.teacher_name?.[0] || 'T'}
              </div>
              <span className="text-sm font-black text-gray-700">أ / {lesson.teacher_name || 'المدرس'}</span>
            </div>
          </div>

          <div className="flex flex-col items-end">
            <div className="flex items-center gap-3 text-[9px] text-gray-400 font-black mb-1">

              {getLessonDuration(lesson) > 0 && (
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  {formatDuration(getLessonDuration(lesson))}
                </div>
              )}

              {lesson.bunny_video_id && (
                <div className="flex items-center gap-1">
                  <Eye size={12} className="text-blue-500" />
                  {getLessonViews(lesson)} مشاهدة
                </div>
              )}

            </div>
            <StarRating rating={lesson.user_rating || 0} onRate={(rating) => onRate && onRate(lesson.id, rating)} />
            <span className="text-[9px] text-gray-400 font-black mt-1 uppercase tracking-tighter">تقييم ممتاز</span>
          </div>
        </div>

        <div className="flex gap-3 mt-2">
          <button
            onClick={() => onView && onView(lesson.id)}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-black py-4 px-6 rounded-2xl flex items-center justify-center gap-3 transition-all transform active:scale-95 shadow-xl shadow-primary-100 group/btn"
          >
            <PlayCircle size={20} fill="currentColor" className="opacity-40 group-hover/btn:opacity-100 transition-opacity" />
            {lesson.progress > 0 ? 'استكمال الدرس' : 'ابدأ المشاهدة'}
          </button>
          <button
            onClick={() => onRate && onRate(lesson.id, lesson.user_rating || 0)}
            className="p-4 border border-gray-100 rounded-2xl hover:bg-white hover:border-yellow-200 hover:shadow-xl text-gray-400 hover:text-yellow-500 transition-all duration-300 transform active:scale-90"
            title="قيم هذا المحتوى"
          >
            <Star size={24} className={lesson.user_rating > 0 ? 'fill-yellow-400 text-yellow-400' : ''} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentLessonCard;
